import React from 'react';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import { makeStyles } from "@material-ui/core/styles";
import { InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import ActionButton from '../button/actionButton';
import TextField from '../input/textField';
import { client } from "../../utils/data/graphql/client";
import { createOtcOrderAdminMutation } from "../../utils/data/graphql/otc/mutations";
import { useAuth0 } from "@auth0/auth0-react";

const useStyles = makeStyles((theme) => ({
    root: {
        top: '50%',
        left: '50%',
        position: 'absolute',
        transform: 'translate(-50%, -50%)',
        width: '416px',
        maxHeight: '90vh',
        minHeight: '706px',
        borderRadius: '20px',
        background: theme.background.dialog,
        overflowY: 'auto',
    },
    cardContent: {
        margin: '32px !important',
        padding: '0 !important',
        display: 'flex',
        justifyContent: 'center',
    },
    backdrop: {
        ['@supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none))']: {
            '-webkit-backdrop-filter': 'blur(2em)',
            backdropFilter: 'blur(2em)',
            backgroundColor: 'rgb(0 0 0 / 33%) !important',
        }
    },
    content: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginTop: '42px',
    },
    closeButton: {
        position: 'absolute',
        top: theme.spacing(4),
        right: theme.spacing(3),
    },
    heading: {
        marginTop: '12px',
        alignSelf: 'start'
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        margin: '32px 0px 32px 0px'
    },
    input: {
        margin: '12px 0px 12px 0px !important',
    },
    selectLabel: {
        marginBottom: '4px'
    },
    confirmButton: {
        marginBottom: '40px',
        position: 'relative',
    },
}));

const SendStablecoinRecipientDetailsDialog = ({ onClose, onComplete, initialValues = {} }) => {
    const classes = useStyles();
    const { user } = useAuth0();
    const [loading, setLoading] = React.useState(false);
    const [submitAttempted, setSubmitAttempted] = React.useState(false);
    const [customerId, setCustomerId] = React.useState(initialValues.customerId || '');
    const [depositType] = React.useState(initialValues.depositType || 'STABLECOIN');
    
    // Source fields (which balance to deduct from)
    const [sourceCurrency, setSourceCurrency] = React.useState('USDⓈ');
    const [sourceAmount, setSourceAmount] = React.useState('');
    
    // Payout fields (what recipient receives - typically stablecoins)
    const [payoutCurrency, setPayoutCurrency] = React.useState('USDT');
    const [payoutAmount, setPayoutAmount] = React.useState('');
    
    // Recipient fields (hardcoded for stablecoins)
    const [network, setNetwork] = React.useState('Ethereum');
    const [walletAddress, setWalletAddress] = React.useState('');
    
    // Transaction date from initial values
    const [transactionDate] = React.useState(initialValues.date || new Date().toISOString().substring(0,10));

    // Hardcoded options
    const payoutCurrencies = ['USDT', 'USDC'];
    const NETWORK_OPTIONS = ['Ethereum', 'Tron', 'Polygon', 'Solana', 'Arbitrum', 'Optimism', 'Base'];

    // Auto-fill payout amount for stablecoins (1:1 conversion with USD)
    // User can still manually override the value
    React.useEffect(() => {
        if (sourceAmount && !isNaN(parseFloat(sourceAmount))) {
            setPayoutAmount(sourceAmount);
        } else {
            setPayoutAmount('');
        }
    }, [sourceAmount]);

    const isPositiveNumber = (val) => {
        const n = parseFloat(val);
        return !Number.isNaN(n) && n > 0;
    };

    const isFormValid = () => {
        return Boolean(
            customerId &&
            sourceCurrency &&
            isPositiveNumber(sourceAmount) &&
            payoutCurrency &&
            isPositiveNumber(payoutAmount) &&
            network &&
            walletAddress
        );
    };

    const getValidationMessage = () => {
        if (!customerId) return 'Customer ID is required.';
        if (!sourceCurrency || !isPositiveNumber(sourceAmount)) return 'Please enter valid source currency and amount.';
        if (!payoutCurrency || !isPositiveNumber(payoutAmount)) return 'Please enter valid payout currency and amount.';
        if (!network) return 'Please select a network.';
        if (!walletAddress) return 'Please enter a wallet address.';
        return '';
    };

    return (
        <Modal open={true} onClose={onClose} BackdropProps={{className:classes.backdrop}}>
            <Fade in={true}>
                <Card className={classes.root}>
                    <CardContent className={classes.cardContent}>
                        <ActionButton variant="secondary" size="lg"
                                      className={classes.closeButton}
                                      onClick={onClose}
                        >
                            Close
                        </ActionButton>
                        <div className={classes.content}>
                            <Typography variant="h3" className={classes.heading}>Create a pending send transaction</Typography>
                            <Typography variant="body1" style={{ alignSelf: 'start', marginTop: '8px' }}>
                                Enter the stablecoin transaction details below.
                            </Typography>
                            <div className={classes.inputContainer}>
                                <TextField
                                    className={classes.input}
                                    name='customerId'
                                    type='text'
                                    value={customerId}
                                    label={'Customer ID'}
                                    InputLabelProps={{shrink: false, required: false, disableAnimation: true}}
                                    onChange={(e) => setCustomerId(e.target.value)}
                                />
                                
                                {/* Source Section */}
                                <Typography variant="overline" style={{marginTop: '16px', fontSize: '0.75rem', fontWeight: 600}}>
                                    Source (Balance Deduction)
                                </Typography>
                                <TextField
                                    className={classes.input}
                                    name='sourceCurrency'
                                    type='text'
                                    value="USDⓈ"
                                    label="Source Currency"
                                    InputLabelProps={{shrink: false, disableAnimation: true}}
                                    InputProps={{
                                        style: { color: '#ffffff' }
                                    }}
                                    disabled
                                    helperText="All balances are maintained in USDⓈ"
                                />
                                <TextField
                                    className={classes.input}
                                    name='sourceAmount'
                                    type='number'
                                    value={sourceAmount}
                                    label={'Source Amount'}
                                    placeholder="100.00"
                                    InputLabelProps={{shrink: false, required: true, disableAnimation: true}}
                                    onChange={(e) => setSourceAmount(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    error={submitAttempted && (!sourceAmount || parseFloat(sourceAmount) <= 0)}
                                    helperText={submitAttempted && (!sourceAmount || parseFloat(sourceAmount) <= 0) ? "Please enter a valid amount" : ""}
                                />
                                
                                {/* Payout Section */}
                                <Typography variant="overline" style={{marginTop: '16px', fontSize: '0.75rem', fontWeight: 600}}>
                                    Payout (Recipient Receives)
                                </Typography>
                                <InputLabel htmlFor='payout-currency-select' className={classes.selectLabel} style={{marginTop: '8px'}}>
                                    Payout Currency
                                </InputLabel>
                                <Select
                                    className={classes.input}
                                    id='payout-currency-select'
                                    name='payoutCurrency'
                                    value={payoutCurrency}
                                    onChange={(e) => setPayoutCurrency(e.target.value)}
                                    fullWidth
                                >
                                    {payoutCurrencies.map(curr => <MenuItem key={curr} value={curr}>{curr}</MenuItem>)}
                                </Select>
                                <TextField
                                    className={classes.input}
                                    name='payoutAmount'
                                    type='number'
                                    value={payoutAmount}
                                    label={'Payout Amount'}
                                    placeholder="150.00"
                                    InputLabelProps={{shrink: false, required: true, disableAnimation: true}}
                                    onChange={(e) => setPayoutAmount(e.target.value)}
                                    onWheel={(e) => e.target.blur()}
                                    error={submitAttempted && (!payoutAmount || parseFloat(payoutAmount) <= 0)}
                                    helperText={
                                        submitAttempted && (!payoutAmount || parseFloat(payoutAmount) <= 0) 
                                            ? "Please enter a valid amount" 
                                            : "Auto-filled (1:1 with USD). You can modify if needed."
                                    }
                                />
                                
                                {/* Recipient Details Section */}
                                <Typography variant="overline" style={{marginTop: '16px', fontSize: '0.75rem', fontWeight: 600}}>
                                    Recipient Details
                                </Typography>
                                
                                <InputLabel htmlFor='network-select' className={classes.selectLabel} style={{marginTop: '8px'}}>
                                    Network *
                                </InputLabel>
                                <Select
                                    className={classes.input}
                                    id='network-select'
                                    name='network'
                                    value={network}
                                    onChange={(e) => setNetwork(e.target.value)}
                                    fullWidth
                                    error={submitAttempted && !network}
                                >
                                    {NETWORK_OPTIONS.map(net => (
                                        <MenuItem key={net} value={net}>{net}</MenuItem>
                                    ))}
                                </Select>
                                
                                <TextField
                                    className={classes.input}
                                    name='walletAddress'
                                    type='text'
                                    value={walletAddress}
                                    label={'Wallet Address'}
                                    placeholder="0x..."
                                    InputLabelProps={{shrink: false, required: true, disableAnimation: true}}
                                    onChange={(e) => setWalletAddress(e.target.value)}
                                    error={submitAttempted && !walletAddress}
                                    helperText={submitAttempted && !walletAddress ? "Please enter a wallet address" : ""}
                                />
                            </div>
                            {submitAttempted && !isFormValid() && (
                              <Typography variant="body2" style={{ color: '#d32f2f', alignSelf: 'start', marginTop: 8 }}>
                                {getValidationMessage()}
                              </Typography>
                            )}
                            <ActionButton
                                variant="primary"
                                size="lg"
                                className={classes.confirmButton}
                                onClick={async () => {
                                    setSubmitAttempted(true);
                                    if (!isFormValid()) {
                                        return;
                                    }
                                    setLoading(true);
                                    try {
                                        await client.mutate({
                                            mutation: createOtcOrderAdminMutation,
                                            variables: {
                                                input: {
                                                    customerId: customerId,
                                                    side: 'WITHDRAW',
                                                    currency: payoutCurrency,
                                                    amount: parseFloat(payoutAmount),
                                                    debitAmountUsd: parseFloat(sourceAmount),
                                                    settlementType: 'STABLECOIN',
                                                    recipientDetails: {
                                                        network: network,
                                                        walletAddress: walletAddress
                                                    },
                                                    createdBy: user?.email,
                                    metadata: {
                                        transactionDate: transactionDate
                                    }
                                                }
                                            }
                                        });
                                        onClose();
                                        onComplete && onComplete(true);
                                    } catch (e) {
                                        console.error(e);
                                        onComplete && onComplete(false);
                                    } finally {
                                        setLoading(false);
                                    }
                                }}
                                disabled={!isFormValid() || loading}
                            >
                                {loading ? 'Processing...' : 'Done'}
                            </ActionButton>
                        </div>
                    </CardContent>
                </Card>
            </Fade>
        </Modal>
    )
}

export default SendStablecoinRecipientDetailsDialog;

